import { Stack } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';

import {
  BasicContactWithIdDto,
  ContactItemDto,
  CustomerItemDto,
  CustomerPropertiesDto,
} from '@/api/generated';
import { DetailsViewItem } from '@/components/detailView/DetailsViewItem.comp.tsx';
import { DetailsViewWrapper } from '@/components/detailView/DetailsViewWrapper.comp.tsx';
import { useDictionariesContext } from '@/contexts/dictionaries/Dictionaries.context.ts';
import { LocalStorageKey, localStorageUtil } from '@/utils/localStorage/localStorage.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { CustomerShortDetails } from '@/views/customers/list/components/CustomerShortDetails.comp.tsx';
import { customerStatusUtil } from '@/views/customers/status/customerStatus.util.ts';
import { ProcessAddContactDialog } from '@/views/processesDetails/components/dialog/addContact/ProcessAddContactDialog.comp.tsx';
import { ProcessAddCustomerDialog } from '@/views/processesDetails/components/dialog/addCustomer/ProcessAddCustomerDialog.comp.tsx';

type SelectCustomerOrContactForProcessCommonProps = {
  customerTableKey: string;
  onSelectContact: (contact: ContactItemDto) => void;
  onSelectCustomer: (customer: CustomerItemDto) => void;
  urlStateEnabled?: boolean;
};

export type SelectCustomerOrContactForProcessProps = SelectCustomerOrContactForProcessCommonProps &
  (
    | {
        contact: Partial<BasicContactWithIdDto | ContactItemDto> | null;
        customer: Partial<CustomerPropertiesDto> | null;
        hideDetails?: false;
      }
    | { contact?: never; customer?: never; hideDetails: true }
  );

export const SelectCustomerOrContactForProcess: React.FC<
  SelectCustomerOrContactForProcessProps
> = props => {
  const {
    contact,
    customer,
    customerTableKey,
    hideDetails,
    onSelectContact,
    onSelectCustomer,
    urlStateEnabled = true,
  } = props;
  const { t } = useTranslationWithPrefix('views.processesCreate.form.customer');
  const { t: tPicker } = useTranslationWithPrefix('views.customers.status.processPicker');
  const [isCustomerDialogOpen, setIsCustomerDialogOpen] = useState(false);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);

  const handleCloseDialog = () => {
    setIsCustomerDialogOpen(false);
    setIsContactDialogOpen(false);
  };

  const handleOpenCustomerDialog = () => {
    localStorageUtil.setItem({
      key: LocalStorageKey.CUSTOM_TABLE,
      subKey: customerTableKey,
      value: '',
    });
    setIsCustomerDialogOpen(true);
  };

  const handleOpenContactDialog = () => {
    setIsContactDialogOpen(true);
  };

  const handleSelectCustomer = (customer: CustomerItemDto) => {
    onSelectCustomer(customer);
    handleCloseDialog();
  };

  const handleSelectContact = (contact: ContactItemDto) => {
    onSelectContact(contact);
    handleCloseDialog();
  };

  return (
    <>
      <Divider flexItem />
      <Stack direction="row" spacing={2}>
        <Button onClick={handleOpenCustomerDialog}>{t('button.chooseCustomer')}</Button>
        <Button onClick={handleOpenContactDialog}>{t('button.chooseContact')}</Button>
      </Stack>
      {!hideDetails && (
        <>
          <ContactDetails contact={contact ?? null} />
          <Box
            sx={{
              pt: customer ? 2 : 0,
            }}
          >
            <CustomerShortDetails customer={customer ?? null} title={t('customerSectionTitle')} />
          </Box>
          {/* e.g. the customer of a chosen contact is a Prospekt: the backend refuses it (409 CUSTOMER_NOT_APPROVED) */}
          {!customerStatusUtil.canBeProcessCustomer(customer?.status) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {tPicker('selectedCustomerNotApproved')}
            </Alert>
          )}
        </>
      )}
      {isCustomerDialogOpen && (
        <ProcessAddCustomerDialog
          onCloseClick={handleCloseDialog}
          onlyProcessCustomers
          onSelectCustomer={handleSelectCustomer}
          tableKey={customerTableKey}
          urlStateEnabled={urlStateEnabled}
        />
      )}
      {isContactDialogOpen && (
        <ProcessAddContactDialog
          onCloseClick={handleCloseDialog}
          onSelectContact={handleSelectContact}
          urlStateEnabled={urlStateEnabled}
        />
      )}
    </>
  );
};

type ContactDetailsProps = {
  contact: Partial<BasicContactWithIdDto | ContactItemDto> | null;
};

const ContactDetails: React.FC<ContactDetailsProps> = ({ contact }) => {
  const { t } = useTranslationWithPrefix('views.processesCreate.form.customer');
  const { USERS_SHORT } = useDictionariesContext();
  const associatedCustomer = contact && 'customer' in contact ? contact.customer : undefined;

  if (!contact) {
    return null;
  }

  return (
    <Box
      sx={{
        pt: 2,
      }}
    >
      <Paper sx={{ p: 2 }}>
        <Typography sx={{ pb: 2 }} variant="f12Regular">
          {t('contactSectionTitle')}
        </Typography>
        <DetailsViewWrapper>
          <DetailsViewItem title={t('firstName')} value={contact.firstName} />
          <DetailsViewItem title={t('lastName')} value={contact.lastName} />
          {associatedCustomer?.name && (
            <DetailsViewItem title={t('customerName')} value={associatedCustomer.name} />
          )}
          <DetailsViewItem
            title={t('contactOwnerId')}
            value={USERS_SHORT.find(it => it.value === contact.contactOwnerId)?.label}
          />
        </DetailsViewWrapper>
      </Paper>
    </Box>
  );
};
